import { from, Observable } from "rxjs"

import { SUCCESS_EQUITY_INSTRUMENT_RESULT } from "@/generated/TradingGateway"

import type {
  EquityInstrumentResult,
  EquityInstrumentUpdate,
  Instrument,
  InstrumentRequest,
  SuccessEquityInstrumentResult,
} from "../EquityInstrumentService"

export const mockInstruments: Instrument[] = [
  {
    symbol: "AAPL",
    name: "Apple Inc. Common Stock",
    displayName: "Apple",
    exchange: "NASDAQ",
    sector: "Technology",
    industry: "Computer Manufacturing",
    marketCapitalization: "2786950000000",
    tradable: true,
    status: "ACTIVE",
  },
  {
    symbol: "AMZN",
    name: "Amazon.com, Inc. Common Stock",
    displayName: "Amazon",
    exchange: "NASDAQ",
    sector: "Consumer Discretionary",
    industry: "Catalog/Specialty Distribution",
    marketCapitalization: "1448509034250",
    tradable: true,
    status: "ACTIVE",
  },
  {
    symbol: "F",
    name: "Ford Motor Company",
    displayName: "Ford Motor",
    exchange: "NYSE",
    sector: "Consumer Discretionary",
    industry: "Auto Manufacturing",
    marketCapitalization: "50468064841",
    tradable: true,
    status: "ACTIVE",
  },
  {
    symbol: "GE",
    name: "General Electric Company",
    displayName: "General Electric",
    exchange: "NYSE",
    sector: "Technology",
    industry: "Consumer Electronics/Appliances",
    marketCapitalization: "127630000000",
    tradable: true,
    status: "ACTIVE",
  },
  {
    symbol: "META",
    name: "Meta Platforms, Inc. Class A Common Stock",
    displayName: "Meta",
    exchange: "NASDAQ",
    sector: "Technology",
    industry: "Computer Software: Programming Data Processing",
    marketCapitalization: "807720000000",
    tradable: true,
    status: "ACTIVE",
  },
  {
    symbol: "MSFT",
    name: "Microsoft Corporation Common Stock",
    displayName: "Microsoft",
    exchange: "NASDAQ",
    sector: "Technology",
    industry: "Computer Software: Prepackaged Software",
    marketCapitalization: "2453456576279",
    tradable: true,
    status: "ACTIVE",
  },
  {
    symbol: "NFLX",
    name: "Netflix, Inc. Common Stock",
    displayName: "Netflix",
    exchange: "NASDAQ",
    sector: "Consumer Discretionary",
    industry: "Consumer Electronics/Video Chains",
    marketCapitalization: "175902671342",
    tradable: true,
    status: "ACTIVE",
  },
  {
    symbol: "NVDA",
    name: "NVIDIA Corporation Common Stock",
    displayName: "NVIDIA",
    exchange: "NASDAQ",
    sector: "Technology",
    industry: "Semiconductors",
    marketCapitalization: "1084330000000",
    tradable: true,
    status: "ACTIVE",
  },
  {
    symbol: "TSLA",
    name: "Tesla, Inc. Common Stock",
    displayName: "Tesla",
    exchange: "NASDAQ",
    sector: "Consumer Discretionary",
    industry: "Auto Manufacturing",
    marketCapitalization: "870912341800",
    tradable: true,
    status: "ACTIVE",
  },
]

export const featuredInstrumentSymbols = mockInstruments.map(
  (instrument) => instrument.symbol,
)

const toSuccessResult = (
  instrument: Instrument,
): SuccessEquityInstrumentResult => ({
  type: SUCCESS_EQUITY_INSTRUMENT_RESULT,
  payload: {
    payload: instrument,
  } satisfies EquityInstrumentUpdate,
})

// Matches the gateway default when the caller sends no positive limit.
const DEFAULT_LIMIT = 20

const mockInstrumentsBySymbol = new Map(
  mockInstruments.map((instrument) => [instrument.symbol, instrument]),
)

interface RankedInstrument {
  instrument: Instrument
  rank: number
}

const SYMBOL_MATCH_RANK = -1

const rankInstrument = (
  instrument: Instrument,
  query: string,
): number | null => {
  if (instrument.symbol.toLowerCase().includes(query)) {
    return SYMBOL_MATCH_RANK
  }

  const nameMatchIndex = instrument.name.toLowerCase().indexOf(query)
  return nameMatchIndex === -1 ? null : nameMatchIndex
}

/**
 * Mirrors InstrumentRepositoryPlugin.searchByString: case-insensitive
 * `contains` against symbol and name (never displayName), ranking symbol
 * matches ahead of name matches, then by how early the match falls in the name.
 */
const searchByString = (searchStr: string, limit: number): Instrument[] => {
  const query = searchStr.toLowerCase()

  return mockInstruments
    .map((instrument) => ({
      instrument,
      rank: rankInstrument(instrument, query),
    }))
    .filter((match): match is RankedInstrument => match.rank !== null)
    .sort(
      (a, b) =>
        a.rank - b.rank || a.instrument.name.localeCompare(b.instrument.name),
    )
    .slice(0, limit)
    .map(({ instrument }) => instrument)
}

const getBySymbols = (symbols: string[], limit: number): Instrument[] =>
  symbols
    .map((symbol) => mockInstrumentsBySymbol.get(symbol))
    .filter((instrument): instrument is Instrument => instrument !== undefined)
    .slice(0, limit)

/**
 * Mirrors InstrumentServiceImpl.getInstruments: search string wins, then an
 * explicit symbol list, then every instrument.
 */
export function searchMockInstruments({
  searchStr,
  symbols,
  limit,
}: InstrumentRequest): Instrument[] {
  const effectiveLimit = limit > 0 ? limit : DEFAULT_LIMIT

  if (searchStr.length > 0) {
    return searchByString(searchStr, effectiveLimit)
  }

  if (symbols.length > 0) {
    return getBySymbols(symbols, effectiveLimit)
  }

  return mockInstruments.slice(0, effectiveLimit)
}

export function getMockEquityInstrumentResult(
  request: InstrumentRequest,
): Observable<EquityInstrumentResult> {
  return from(searchMockInstruments(request).map(toSuccessResult))
}
